import React, { useCallback, useEffect, useMemo, useState } from "react";
import { X, Send, AlertCircle } from "lucide-react";
import { toast } from "react-toastify";
import { getTemplates } from "../../apis/template";
import { sendTemplateMessage } from "../../apis/chat";
import SearchableSelect from "../ui/SearchableSelect";
import ThemeLoader from "../ui/ThemeLoader";
import TemplatePreview from "../templateCreator/TemplatePreview";
import { buildTemplatePreviewState } from "../../utils/campaignTemplatePreview";
import {
  extractRequiredParameters,
  getSampleForParam,
  buildUserVarsFromFormValues,
  validateTemplateVariableValues,
} from "../../utils/campaignTemplateUtils";

const filterApprovedTemplates = (rawTemplates) => {
  const approved = rawTemplates.filter(
    (t) => t.status === "approved" || t.status === "APPROVED"
  );
  const notAuth = approved.filter(
    (t) => (t.category || "").toLowerCase() !== "authentication"
  );
  const withQuality = notAuth.filter((t) => {
    const q = (t.quality_score || "").toString().toUpperCase();
    return q !== "RED";
  });
  return withQuality.length > 0 ? withQuality : notAuth;
};

const SendTemplateModal = ({
  isOpen,
  onClose,
  phoneNumberId,
  customerPhone,
  customerName,
  onSent,
}) => {
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [variableValues, setVariableValues] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [viewMode, setViewMode] = useState("edit");

  const selectedTemplate = useMemo(
    () =>
      templates.find(
        (t) => String(t.id).trim() === String(selectedTemplateId).trim()
      ) || null,
    [templates, selectedTemplateId]
  );

  const templateParams = useMemo(
    () => (selectedTemplate ? extractRequiredParameters(selectedTemplate) : []),
    [selectedTemplate]
  );

  const previewState = useMemo(() => {
    if (!selectedTemplate) return null;
    if (viewMode === "preview") {
      return buildTemplatePreviewState(selectedTemplate, variableValues);
    }
    return buildTemplatePreviewState(selectedTemplate, {});
  }, [selectedTemplate, variableValues, viewMode]);

  const isEditing = viewMode === "edit";

  const variableKeyResolver = useCallback(
    (component, varName) => {
      if (!selectedTemplate) return varName;
      const params = extractRequiredParameters(selectedTemplate);
      const found = params.find((p) => {
        if (component === "BUTTON") {
          return p.component === "BUTTON" && String(p.index) === String(varName);
        }
        if (p.component !== component) return false;
        if (p.paramName && p.paramName === varName) return true;
        if (p.varNum && String(p.varNum) === String(varName)) return true;
        if (p.key === varName) return true;
        return false;
      });
      if (found) return found.key;
      if (component === "HEADER") return `header_${varName}`;
      if (component === "BODY") return `body_${varName}`;
      if (component === "BUTTON") return `button_text_${varName}`;
      return varName;
    },
    [selectedTemplate]
  );

  const templateOptions = useMemo(
    () =>
      templates.map((t) => ({
        value: String(t.id),
        label: t.name,
        searchText: `${t.name} ${t.language || ""} ${t.category || ""}`.toLowerCase(),
      })),
    [templates]
  );

  const selectedTemplateOption = useMemo(
    () =>
      templateOptions.find((o) => o.value === String(selectedTemplateId)) || null,
    [templateOptions, selectedTemplateId]
  );

  const loadTemplates = useCallback(async () => {
    if (!phoneNumberId) return;
    setLoadingTemplates(true);
    try {
      const response = await getTemplates({
        limit: 1000,
        offset: 0,
        phoneId: phoneNumberId,
        status: "approved",
      });
      const raw =
        response.data.metaTemplates || response.data.templates || [];
      setTemplates(filterApprovedTemplates(raw));
    } catch (err) {
      console.error("Failed to load templates for live chat:", err);
      setTemplates([]);
      toast.error("Failed to load templates for this phone number");
    } finally {
      setLoadingTemplates(false);
    }
  }, [phoneNumberId]);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedTemplateId(null);
    setVariableValues({});
    setFieldErrors({});
    setSubmitError(null);
    setViewMode("edit");
    loadTemplates();
  }, [isOpen, loadTemplates]);

  useEffect(() => {
    if (!selectedTemplate) {
      setVariableValues({});
      setFieldErrors({});
      return;
    }
    const params = extractRequiredParameters(selectedTemplate);
    const next = {};
    params.forEach((param) => {
      next[param.key] = getSampleForParam(selectedTemplate, param) || "";
    });
    setVariableValues(next);
    setFieldErrors({});
    setSubmitError(null);
    setViewMode("edit");
  }, [selectedTemplate]);

  const handleVariableChange = (key, value) => {
    setVariableValues((prev) => ({ ...prev, [key]: value }));
    if (fieldErrors[key]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const handleSend = async () => {
    if (!selectedTemplate || !phoneNumberId || !customerPhone) return;

    const validation = validateTemplateVariableValues(
      selectedTemplate,
      variableValues
    );
    if (!validation.valid) {
      setFieldErrors(validation.errors);
      setSubmitError("Fill in all highlighted fields in the preview before sending.");
      setViewMode("edit");
      return;
    }

    setSubmitError(null);
    setSending(true);

    try {
      const userVars = buildUserVarsFromFormValues(
        selectedTemplate,
        variableValues
      );
      const response = await sendTemplateMessage(
        phoneNumberId,
        customerPhone,
        selectedTemplate,
        userVars
      );

      if (response.data?.success) {
        toast.success("Template sent successfully");
        onSent?.(response.data?.data?.message);
        onClose();
      } else {
        const msg =
          response.data?.message ||
          response.data?.errorMessage ||
          response.data?.error ||
          "Failed to send template";
        setSubmitError(msg);
        toast.error(msg);
      }
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Failed to send template";
      setSubmitError(msg);
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="ping-drawer-backdrop" onClick={() => !sending && onClose()} />
      <aside className="ping-drawer lc-tpl-drawer" role="dialog" aria-modal="true" aria-label="Send template">
        <div className="ping-drawer-head">
          <div>
            <h3>Send template</h3>
            <p>To {customerName || customerPhone}</p>
          </div>
          <button
            type="button"
            className="camp-ico"
            onClick={() => !sending && onClose()}
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="ping-drawer-body lc-tpl-body">
          <label>
            Template
            {loadingTemplates ? (
              <span className="lc-tpl-loading">
                <ThemeLoader size="xs" spinnerOnly />
                Loading templates…
              </span>
            ) : (
              <SearchableSelect
                options={templateOptions}
                value={selectedTemplateOption}
                onChange={(opt) => setSelectedTemplateId(opt?.value || null)}
                placeholder="Search approved templates…"
                isClearable
                variant="campaign"
                formatOptionLabel={(opt) => {
                  const tpl = templates.find(
                    (t) => String(t.id) === String(opt.value)
                  );
                  if (!tpl) return opt.label;
                  const meta = [tpl.language, tpl.category]
                    .filter(Boolean)
                    .join(" · ");
                  return (
                    <div>
                      <div className="font-semibold text-sm">{opt.label}</div>
                      {meta && (
                        <div className="text-[11px] text-gray-500">{meta}</div>
                      )}
                    </div>
                  );
                }}
              />
            )}
          </label>
          <p className="lc-tpl-hint">Approved templates for this WhatsApp number only.</p>

          {previewState ? (
            <TemplatePreview
              state={previewState}
              editable={isEditing}
              variableValues={variableValues}
              variableErrors={fieldErrors}
              onVariableChange={handleVariableChange}
              variableKeyResolver={variableKeyResolver}
              viewMode={viewMode}
              onViewModeChange={setViewMode}
            />
          ) : (
            <div className="lc-tpl-empty">
              Select a template to preview and edit variables
            </div>
          )}

          {selectedTemplate && templateParams.length === 0 && (
            <p className="lc-tpl-hint">This template has no variables — review the preview and send.</p>
          )}

          {submitError && (
            <div className="ping-banner red">
              <AlertCircle size={14} />
              <div>{submitError}</div>
            </div>
          )}
        </div>

        <div className="ping-drawer-foot">
          <button
            type="button"
            className="ping-btn ping-btn-ghost ping-btn-sm"
            onClick={onClose}
            disabled={sending}
          >
            Cancel
          </button>
          <button
            type="button"
            className="ping-btn ping-btn-green ping-btn-sm disabled:opacity-50"
            onClick={handleSend}
            disabled={!selectedTemplate || sending}
          >
            {sending ? <ThemeLoader size="xs" spinnerOnly /> : <Send size={14} />}
            {sending ? "Sending…" : "Send template"}
          </button>
        </div>
      </aside>
    </>
  );
};

export default SendTemplateModal;
